import React, { useRef, useState, useEffect } from "react";

export interface DropdownOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  placeholder?: string;
}

export default function CustomDropdown({ value, options, onChange, searchable = false, searchPlaceholder = "Search", placeholder = "Select" }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const selected = options.find((o) => o.value === value);
  const filtered = query ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase())) : options;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full h-[38px] border rounded-lg pl-3.5 pr-9 text-[13px] bg-white text-left flex items-center relative ${open ? "border-teal-dark ring-2 ring-teal-dark/15" : "border-[#D8DEE6]"}`}
      >
        <span className={selected ? "text-ink" : "text-ink3"}>{selected?.label ?? placeholder}</span>
        <span className="absolute right-3.5 top-1/2 w-[7px] h-[7px] border-r-2 border-b-2 border-ink3 -mt-[6px] rotate-45" />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-[#D8DEE6] rounded-[10px] shadow-xl z-50 overflow-hidden">
          {searchable && (
            <div className="p-2.5 border-b border-[#EEF1F4]">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                className="w-full h-[34px] border border-[#D8DEE6] rounded-lg px-3 text-[13px] bg-white focus:outline-none focus:border-teal-dark"
              />
            </div>
          )}
          <div className="max-h-[280px] overflow-y-auto py-1.5">
            {filtered.length === 0 ? (
              <div className="px-4 py-2 text-[13px] text-ink3">No matches</div>
            ) : (
              filtered.map((opt) => {
                const isSel = opt.value === value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => { onChange(opt.value); setOpen(false); }}
                    className={`w-full text-left py-2 pl-9 pr-3.5 text-[13px] relative hover:bg-[#EEF4FB] ${isSel ? "bg-[#F0F6FC] font-semibold text-ink" : "text-ink"}`}
                  >
                    {isSel && <span className="absolute left-3.5 top-1/2 w-[10px] h-[6px] border-l-2 border-b-2 border-teal-dark -mt-[5px] -rotate-45" />}
                    {opt.label}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}